import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Space, theme } from 'antd';

import {
  useCharbeePlanId,
  useIsAccountOwner,
  useIsCancellationScheduled,
  usePrerenderUserId,
  useSelector,
  useUserRole,
} from '../../../hooks/usePrerenderUser';
import USER_ROLES from '../../auth/userRoles';
import useTrackBillingEvent from '../hooks/useTrackBillingEvent';
import { dates } from '~library/dates';

const MANAGE_SUBSCRIPTION_HREF = '/billing/plans';
const STORAGE_KEY_PREFIX = 'billing.subscriptionScheduledCancellationBanner.dismissed';
const RESURFACE_WINDOW_IN_MS = 7 * 24 * 60 * 60 * 1000;

type BannerDismissalState = {
  dismissed: boolean;
  windowDismissedForCancellationDate: string | null;
};

const DEFAULT_DISMISSAL_STATE: BannerDismissalState = {
  dismissed: false,
  windowDismissedForCancellationDate: null,
};

const getStorageKey = (userId: number) => `${STORAGE_KEY_PREFIX}.${userId}`;

const readDismissalState = (storageKey: string): BannerDismissalState => {
  const rawValue = localStorage.getItem(storageKey);
  if (!rawValue) {
    return DEFAULT_DISMISSAL_STATE;
  }

  try {
    const parsedValue: unknown = JSON.parse(rawValue);
    if (
      typeof parsedValue === 'object'
      && parsedValue !== null
      && 'dismissed' in parsedValue
      && typeof parsedValue.dismissed === 'boolean'
    ) {
      return {
        dismissed: parsedValue.dismissed,
        windowDismissedForCancellationDate: (
          'windowDismissedForCancellationDate' in parsedValue
          && typeof parsedValue.windowDismissedForCancellationDate === 'string'
        )
          ? parsedValue.windowDismissedForCancellationDate
          : (
            'windowDismissedForBillingDate' in parsedValue
            && typeof parsedValue.windowDismissedForBillingDate === 'string'
          )
            ? parsedValue.windowDismissedForBillingDate
          : (
            'ownerWindowDismissedForBillingDate' in parsedValue
            && typeof parsedValue.ownerWindowDismissedForBillingDate === 'string'
          )
            ? parsedValue.ownerWindowDismissedForBillingDate
            : null,
      };
    }
  } catch {
    return DEFAULT_DISMISSAL_STATE;
  }

  return DEFAULT_DISMISSAL_STATE;
};

function SubscriptionScheduledCancellationBanner() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const isCancellationScheduled = useIsCancellationScheduled();
  const cancelledAt = useSelector((state) => state.prerenderUser.cancelledAt);
  const planName = useSelector((state) => state.prerenderUser.plan.name);
  const isAccountOwner = useIsAccountOwner();
  const userId = usePrerenderUserId();
  const userRole = useUserRole();
  const trackBillingEvent = useTrackBillingEvent();
  const chargebeePlanId = useCharbeePlanId();
  const storageKey = React.useMemo(() => getStorageKey(userId), [userId]);
  const [dismissalState, setDismissalState] = React.useState<BannerDismissalState>(() => readDismissalState(storageKey));

  React.useEffect(() => {
    setDismissalState(readDismissalState(storageKey));
  }, [storageKey]);


  const onKeepAccessClick = () => {
    trackBillingEvent('KeepAccessClicked', {
      subscription_plan: chargebeePlanId,
      location: 'global_banner',
    });
    navigate(`/billing/plans/?selectedPlan=${chargebeePlanId}`);
  };


  if (!isCancellationScheduled || !cancelledAt) {
    return null;
  }

  const cancellationDateIso = cancelledAt.toISOString();
  const isInsideOwnerResurfaceWindow = (cancelledAt.getTime() - Date.now()) <= RESURFACE_WINDOW_IN_MS;
  const shouldHideBannerBecauseDismissed = dismissalState.dismissed && (
    !isAccountOwner
    || !isInsideOwnerResurfaceWindow
    || dismissalState.windowDismissedForCancellationDate === cancellationDateIso
  );

  if (shouldHideBannerBecauseDismissed) {
    return null;
  }

  const onDismiss = () => {
    const nextDismissalState: BannerDismissalState = {
      dismissed: true,
      windowDismissedForCancellationDate: (
        isAccountOwner
        && isInsideOwnerResurfaceWindow
      )
        ? cancellationDateIso
        : null,
    };

    localStorage.setItem(storageKey, JSON.stringify(nextDismissalState));
    setDismissalState(nextDismissalState);
  };

  const formattedDate = dates.dayOfPointInTime(cancelledAt);
  const isRestrictedBillingRole = [USER_ROLES.GUEST, USER_ROLES.TEAM_MEMBER].includes(userRole);
  const description = isRestrictedBillingRole
    ? 'Renew to avoid any service interruptions, please contact your Account Owner to manage or renew the subscription.'
    : 'To get back up and running, please renew your subscription.';

  return (
    <Alert
      showIcon
      type="warning"
      closable
      onClose={onDismiss}
      style={{ margin: token.margin }}
      message={`Your subscription is scheduled to end on ${formattedDate}.`}
      description={description}
      action={!isRestrictedBillingRole ? (
        <Space direction="vertical" size={4} align="center">
          <Button
            type="primary"
            onClick={onKeepAccessClick}
          >
            Keep {planName} Plan
          </Button>
          <Link style={{ color: token.colorPrimary }} to={MANAGE_SUBSCRIPTION_HREF}>
            Manage subscription
          </Link>
        </Space>
      ) : undefined}
    />
  );
}

export default SubscriptionScheduledCancellationBanner;
